import lootboxCardSrc from '@/assets/game-cards/lootbox-card.png?w=192&format=webp&quality=70&as=src&imagetools';
import lootboxCardSrcSet from '@/assets/game-cards/lootbox-card.png?w=64;80;96;128;160;192;256&format=webp&quality=70&as=srcset&imagetools';
import coinflipCardSrc from '@/assets/game-cards/coinflip-card.png?w=192&format=webp&quality=70&as=src&imagetools';
import coinflipCardSrcSet from '@/assets/game-cards/coinflip-card.png?w=64;80;96;128;160;192;256&format=webp&quality=70&as=srcset&imagetools';
import diceCardSrc from '@/assets/game-cards/dice-card.png?w=192&format=webp&quality=70&as=src&imagetools';
import diceCardSrcSet from '@/assets/game-cards/dice-card.png?w=64;80;96;128;160;192;256&format=webp&quality=70&as=srcset&imagetools';
import limboCardSrc from '@/assets/game-cards/limbo-card.png?w=192&format=webp&quality=70&as=src&imagetools';
import limboCardSrcSet from '@/assets/game-cards/limbo-card.png?w=64;80;96;128;160;192;256&format=webp&quality=70&as=srcset&imagetools';
import plinkoCardSrc from '@/assets/game-cards/plinko-card.png?w=192&format=webp&quality=70&as=src&imagetools';
import plinkoCardSrcSet from '@/assets/game-cards/plinko-card.png?w=64;80;96;128;160;192;256&format=webp&quality=70&as=srcset&imagetools';
import suiroyaleCardSvg from '@/assets/game-cards/suiroyale-card.svg';

import { LootboxIcon, CoinflipIcon, LeftRightArrowIcon, LimboIcon, PlinkoIcon, SuiroyaleIcon } from '@/components/icons';

export type GameType = 'coinflip' | 'lootbox' | 'limbo' | 'range' | 'plinko' | 'suiroyale' | 'unknown';
export type GameId = Exclude<GameType, 'unknown'>;

export type GameCardImage = {
	src: string;
	srcSet?: string;
	sizes?: string;
};

export interface GameConfig {
	id: GameId;
	name: string;
	title: string;
	description: string;
	icon: React.ComponentType<{ className?: string }>;
	link: string;
	gameType: GameType | 'dice'; // FIXME: Keep 'dice' for backward compatibility for 'range'
	isGame: boolean;
	aliases: string[];
	cardImage: GameCardImage | string;
	cardClass: string;
	tags?: string[];
	shortDescription?: string;
	isNew?: boolean;
}

const CARD_IMAGE_SIZES = '(min-width: 1024px) 160px, (min-width: 768px) 144px, (min-width: 640px) 128px, 96px';
const buildCardImage = (src: string, srcSet: string): GameCardImage => ({
	src,
	srcSet,
	sizes: CARD_IMAGE_SIZES,
});

const ALL_GAMES: GameConfig[] = [
	{
		id: 'suiroyale',
		name: 'SuiRoyale',
		title: 'SuiRoyale',
		description: 'Claim your seats, cheer on your SuiFrens, and chase the last-player-standing jackpots.',
		icon: SuiroyaleIcon,
		link: '/suiroyale',
		gameType: 'suiroyale',
		isGame: true,
		aliases: ['suiroyale'],
		cardImage: suiroyaleCardSvg,
		cardClass: 'game-card-suiroyale',
		tags: ['multiplayer', 'lottery'],
		shortDescription: 'Sweet-styled battle royale raffles.',
		isNew: true,
	},
	{
		id: 'coinflip',
		name: 'Coinflip',
		title: 'Coinflip',
		description: 'Heads or tails? Pick a side and double your stake instantly.',
		icon: CoinflipIcon,
		link: '/coinflip',
		gameType: 'coinflip',
		isGame: true,
		aliases: ['coinflip', 'coinflipv2'],
		cardImage: buildCardImage(coinflipCardSrc, coinflipCardSrcSet),
		cardClass: 'game-card-coinflip',
		tags: ['pvp', 'fast'],
	},
	{
		id: 'lootbox',
		name: 'Lootbox',
		title: 'Lootbox',
		description: 'Open sweet boxes filled with rewards and jackpots.',
		icon: LootboxIcon,
		link: '/lootbox',
		gameType: 'lootbox',
		isGame: true,
		aliases: ['lootbox'],
		cardImage: buildCardImage(lootboxCardSrc, lootboxCardSrcSet),
		cardClass: 'game-card-lootbox',
		tags: ['instant', 'jackpots'],
	},
	{
		id: 'range',
		name: 'Range',
		title: 'Range',
		description: 'Set your target zone and roll the Suifren to land inside it.',
		icon: LeftRightArrowIcon,
		link: '/range',
		gameType: 'dice',
		isGame: true,
		aliases: ['range', 'dice'],
		cardImage: buildCardImage(diceCardSrc, diceCardSrcSet),
		cardClass: 'game-card-range',
		tags: ['custom odds'],
	},
	{
		id: 'limbo',
		name: 'Limbo',
		title: 'Limbo',
		description: 'Pump the balloon as high as you can without popping it!',
		icon: LimboIcon,
		link: '/limbo',
		gameType: 'limbo',
		isGame: true,
		aliases: ['limbo'],
		cardImage: buildCardImage(limboCardSrc, limboCardSrcSet),
		cardClass: 'game-card-limbo',
		tags: ['risk', 'multiplier'],
	},
	{
		id: 'plinko',
		name: 'Plinko',
		title: 'Plinko',
		description: 'Drop the puck through a field of pegs and chase the high multipliers.',
		icon: PlinkoIcon,
		link: '/plinko',
		gameType: 'plinko',
		isGame: true,
		aliases: ['plinko'],
		cardImage: buildCardImage(plinkoCardSrc, plinkoCardSrcSet),
		cardClass: 'game-card-plinko',
		tags: ['classic'],
	},
];

const HIDDEN_GAME_IDS = new Set<GameId>([
	'suiroyale', // Hidden until official launch
]);

export const GAMES: GameConfig[] = ALL_GAMES.filter((game) => !HIDDEN_GAME_IDS.has(game.id));

const ALIAS_MAP = new Map<string, GameConfig>();
ALL_GAMES.forEach((game) => {
	game.aliases.forEach((alias) => ALIAS_MAP.set(alias.toLowerCase(), game));
	ALIAS_MAP.set(game.id.toLowerCase(), game);
});

export const getGameByType = (gameType: string) => ALIAS_MAP.get(gameType.toLowerCase());

export const getGameById = (id: string) => ALIAS_MAP.get(id.toLowerCase());

export const getAllGameTypes = () => Array.from(new Set(Array.from(ALIAS_MAP.keys())));

export const FEATURED_GAMES = GAMES.map((game) => ({
	name: game.name,
	path: game.link,
	cardImage: game.cardImage,
	colorClass: game.cardClass,
}));
